import PropTypes from 'prop-types';
import React from 'react';
import { i18n, moment } from '@international/mastodon-i18n';
import { colors, M } from '@dashboard-experience/mastodon';
import { SSNTraceObject, Report } from 'types/report';
import Panel from '../../../Panels';

interface SSNTraceProps {
  search: SSNTraceObject;
  report: Report;
}

interface Address {
  street: string;
  unit: string | null;
  city: string;
  state: string;
  zipcode: string;
  county: string;
  fromDate: string | null;
  toDate: string | null;
}

const ReportableExceptionNotification: React.FC<{ flag: string }> = ({
  flag,
}) => {
  return (
    <M.ActionableNotification
      inline='classic'
      hideActionButton
      hideCloseButton
      kind='warning'
      title={i18n.getStr(
        `components.Report.SSNTrace.reportable.exceptions.${flag}.title`,
      )}
      subtitle={i18n.getStr(
        `components.Report.SSNTrace.reportable.exceptions.${flag}.text`,
      )}
      style={{
        maxWidth: '100%',
        minWidth: '0px',
        borderColor: colors.archivePink,
        backgroundColor: colors.archivePinkBg,
      }}
    />
  );
};

const formatDateRange = (fromDate: string | null, toDate: string | null) => {
  if (!fromDate) return null;

  const from = moment(fromDate).format('MM[/]YYYY');
  const to = toDate ? moment(toDate).format('MM[/]YYYY') : 'Present';

  return `${from} - ${to}`;
};

// Generate a stable key for an address
const generateAddressKey = (addr: Address, index: number) => {
  // If the address has unique identifiers like street and zipcode, use those
  if (addr.street && addr.zipcode) {
    return `${addr.street}-${addr.zipcode}-${addr.fromDate || 'null'}-${
      addr.toDate || 'null'
    }`;
  }
  // Fall back to index if we can't create a more stable key
  return `address-${index}`;
};

const SSNTrace = ({ search, report }: SSNTraceProps) => {
  const shouldDisplayHistory = report.displayAddressAndAliasHistory;

  const getLookbackTitle = () => {
    const lookbackYears = report.addressHistoryLookbackYears;

    if (!lookbackYears) {
      return 'Address history';
    }

    const numericValue = Number(lookbackYears);
    if (!isNaN(numericValue)) {
      return `Addresses from the last ${lookbackYears} years`;
    }

    return 'Address history';
  };

  const renderAliases = (aliases: any[]) => {
    if (!aliases || !aliases.length) return null;

    const rows = aliases.map(aliasObj => {
      const fullName = [
        aliasObj.firstName,
        aliasObj.middleName,
        aliasObj.lastName,
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <tr key={aliasObj.id} className='align-top'>
          <td className='pb-3 pr-3'>
            <div>{fullName}</div>
          </td>
        </tr>
      );
    });

    return (
      <table
        className='w-100 mt-3'
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <tbody>{rows}</tbody>
      </table>
    );
  };

  const renderAddresses = (addresses: Address[]) => {
    if (!addresses || !addresses.length) return null;

    const rows = addresses.map((addr, i) => (
      <tr key={generateAddressKey(addr, i)} className='align-top'>
        <td className='pb-3 pr-3'>
          <div>
            {addr.street}
            {addr.unit ? ` ${addr.unit}` : ''}
            <br />
            {addr.city}, {addr.state} {addr.zipcode}
          </div>
        </td>

        <td
          className='pb-3 text-secondary text-right'
          style={{ width: '200px' }}
        >
          {formatDateRange(addr.fromDate, addr.toDate)}
        </td>
      </tr>
    ));

    return (
      <table
        className='w-100 mt-3'
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <tbody>{rows}</tbody>
      </table>
    );
  };

  return (
    !!search &&
    search.status !== 'canceled' && (
      <Panel
        title={i18n.getStr('components.ssnTrace')}
        status={search.status}
        noBlock={
          !search.reportable &&
          !(
            (search.addresses?.length > 0 || search.aliases?.length > 0) &&
            shouldDisplayHistory
          )
        }
      >
        {search.reportable && (
          <>
            {search.dobMismatch && (
              <ReportableExceptionNotification flag='dobMismatch' />
            )}
            {search.nameMismatch && (
              <ReportableExceptionNotification flag='nameMismatch' />
            )}
            {search.thinFile && (
              <ReportableExceptionNotification flag='thinFile' />
            )}
            {search.dataMismatch && (
              <ReportableExceptionNotification flag='dataMismatch' />
            )}
            {search.deathIndex && (
              <ReportableExceptionNotification flag='deathIndex' />
            )}
            {search.invalidIssuanceYear && (
              <ReportableExceptionNotification flag='invalidIssuanceYear' />
            )}
          </>
        )}
        {shouldDisplayHistory && (
          <>
            {search.aliases && search.aliases.length > 0 && (
              <div className='mt-3'>
                <div className='md-flex mb-3'>
                  <div>
                    <strong className='mb-3 font-weight-bold'>
                      Other names
                    </strong>
                  </div>
                </div>
                {renderAliases(search.aliases)}
              </div>
            )}
            {search.aliases &&
              search.aliases.length > 0 &&
              search.addresses &&
              search.addresses.length > 0 && <hr className='mb-2 mt-2' />}
            {search.addresses && search.addresses.length > 0 && (
              <div className='mt-3'>
                <div className='md-flex mb-3'>
                  <div>
                    <strong className='mb-3 font-weight-bold'>
                      {getLookbackTitle()}
                    </strong>
                  </div>
                </div>
                {renderAddresses(search.addresses)}
              </div>
            )}
          </>
        )}
      </Panel>
    )
  );
};

SSNTrace.propTypes = {
  search: PropTypes.object,
  report: PropTypes.object,
};

SSNTrace.defaultProps = {
  search: {},
  report: {},
};

export default i18n.renderTranslation()(SSNTrace);
